// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatDate, formatToMoney } from '@shared/ui-kit/utils';
import { ResponsiveBar } from '@nivo/bar';
import { ChartContainer } from '@shared/ui-kit/components';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AccountTransaction } from '@shared/data-access/interfaces/api/brokerage/brokerage';

interface ProcessedChartData {
  date: string;
  inflows: number;
  outflows: number;
  accumulated_balance: number;
  [key: string]: string | number;
}

export interface CashflowChartProps {
  readonly data: AccountTransaction[];
}

export const CashflowChart: FC<CashflowChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();

  const currency = useMemo(() => data[0]?.currency, [data]);

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    value: number;
    date: string;
    visible: boolean;
  } | null>(null);

  const processedData: ProcessedChartData[] = useMemo(() => {
    if (!data.length) return [];

    const monthlyData = data.reduce((acc, transaction) => {
      const date = new Date(transaction.date);
      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, '0')}`;

      if (!acc[monthKey]) {
        acc[monthKey] = {
          date: monthKey,
          inflows: 0,
          outflows: 0,
          transactions: [],
        };
      }

      acc[monthKey].transactions.push(transaction);

      if (transaction.amount && transaction.amount > 0) {
        acc[monthKey].inflows += transaction.amount;
      } else {
        acc[monthKey].outflows += Math.abs(transaction.amount ?? 0);
      }

      return acc;
    }, {} as Record<string, { date: string; inflows: number; outflows: number; transactions: AccountTransaction[] }>);

    const sortedEntries = Object.values(monthlyData).sort((a, b) =>
      a.date.localeCompare(b.date),
    );

    return sortedEntries.map(entry => {
      const monthlyBalance = entry.inflows - entry.outflows;

      return {
        date: entry.date,
        inflows: entry.inflows,
        outflows: entry.outflows,
        accumulated_balance: monthlyBalance,
      };
    });
  }, [data]);

  const { values, keys, colors } = useMemo(() => {
    const values = processedData.flatMap(d => [
      d.inflows,
      d.outflows,
      d.accumulated_balance,
    ]);

    return {
      values,
      keys: ['inflows', 'outflows'],
      colors: ['url(#gradientInflows)', 'url(#gradientOutflows)'],
    };
  }, [processedData]);

  const gradientDefs = [
    {
      id: 'gradientInflows',
      type: 'linearGradient',
      colors: [
        { offset: 0, color: 'var(--color-positive)', opacity: 0.4 },
        { offset: 100, color: 'var(--color-positive)', opacity: 1 },
      ],
      gradientTransform: 'rotate(180 0.5 0.5)',
    },
    {
      id: 'gradientOutflows',
      type: 'linearGradient',
      colors: [
        { offset: 0, color: 'var(--accentRed)', opacity: 0.4 },
        { offset: 100, color: 'var(--accentRed)', opacity: 1 },
      ],
      gradientTransform: 'rotate(180 0.5 0.5)',
    },
  ];

  /* istanbul ignore next */
  const LineLayer = ({ bars, xScale, yScale }: any) => {
    if (!processedData.length || !bars.length) return null;

    // Group bars by their index (date) to find the center of each group
    const barGroups: Record<string, any[]> = {};
    bars.forEach((bar: any) => {
      const key = bar.data.indexValue;
      if (!barGroups[key]) barGroups[key] = [];
      barGroups[key].push(bar);
    });

    const linePoints = processedData
      .map(d => {
        const barsForDate = barGroups[d.date] || [];
        if (barsForDate.length === 0) return null;

        // Calculate center position of the bar group
        const minX = Math.min(...barsForDate.map(b => b.x));
        const maxX = Math.max(...barsForDate.map(b => b.x + b.width));
        const centerX = (minX + maxX) / 2;

        const y = yScale(d.accumulated_balance);
        return { x: centerX, y, data: d };
      })
      .filter(Boolean);

    if (linePoints.length < 2) return null;

    const pathData = linePoints
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point!.x} ${point!.y}`)
      .join(' ');

    const handleMouseEnter = (event: React.MouseEvent, point: any) => {
      setTooltip({
        x: point.x + 60,
        y: point.y + 120,
        value: point.data.accumulated_balance,
        date: point.data.date,
        visible: true,
      });
    };

    const handleMouseLeave = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setTooltip(null);
    };

    return (
      <g>
        <path
          d={pathData}
          fill="none"
          stroke="var(--primaryColor)"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {linePoints.map((point, index) => (
          <circle
            key={index}
            cx={point!.x}
            cy={point!.y}
            r={5}
            fill="var(--primaryColor)"
            stroke="#ffffff"
            strokeWidth={1}
            onMouseEnter={e => handleMouseEnter(e, point)}
            onMouseLeave={handleMouseLeave}
          />
        ))}
      </g>
    );
  };

  const minValue = useMemo(() => Math.min(...values), [values]);

  const maxValue = useMemo(() => Math.max(...values), [values]);
  /* istanbul ignore next */
  const getPadding = (dataLength: number): number => {
    if (dataLength <= 2) return 0.7;
    if (dataLength === 3) return 0.6;
    if (dataLength === 4 || dataLength === 5) return 0.5;
    if (dataLength === 6) return 0.4;
    return 0.3;
  };

  const legendItems = [
    {
      label: t('aggregator.cashflow.final-balance', {
        ns: 'aggregator',
      }),
      color: 'var(--primaryColor)',
    },
    {
      label: t('aggregator.cashflow.inflows', {
        ns: 'aggregator',
      }),
      color: 'var(--color-positive)',
    },
    {
      label: t('aggregator.cashflow.outflows', {
        ns: 'aggregator',
      }),
      color: 'var(--accentRed)',
    },
  ];

  return (
    <ChartContainer data-testid="cashflow-chart">
      {processedData.length > 0 ? (
        <>
          <ResponsiveBar
            data={processedData}
            keys={keys}
            indexBy="date"
            groupMode="grouped"
            margin={{ top: 48, right: 24, bottom: 48, left: 64 }}
            enableGridY={true}
            padding={getPadding(processedData.length)}
            innerPadding={2}
            colors={colors}
            minValue={minValue}
            maxValue={maxValue}
            defs={gradientDefs}
            borderRadius={2}
            layers={['grid', 'axes', 'bars', 'markers', 'legends', LineLayer]}
            axisLeft={{
              tickSize: 0,
              format: /* istanbul ignore next */ (value: number) =>
                formatToMoney(value, currency),
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: processedData.length < 3 ? 55 : 24,
            }}
            valueFormat={(value: number) => formatToMoney(value, currency)}
            enableLabel={false}
            animate={false}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fontSize: 11,
                    fontFamily: 'poppins',
                    fill: 'var(--blueGrayMuted)',
                  },
                },
              },
            }}
            tooltipLabel={value => /* istanbul ignore next */ {
              const date = formatDate(
                value.data.date + '-01',
                'month-year',
                i18n.language,
              );
              const translatedId = t(`aggregator.cashflow.${value.id}` as any, {
                ns: 'aggregator',
              });
              const forText = t('common.for', { ns: 'common' });
              return `${translatedId} ${forText} ${date}`;
            }}
          />

          {tooltip && (
            <div
              className="pointer-events-none absolute z-10 cursor-default rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translate(-50%, -100%)',
              }}
            >
              <div className="mb-1 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-600" />
                <span className="font-medium text-gray-700">
                  {t('aggregator.cashflow.final-balance', {
                    ns: 'aggregator',
                    defaultValue: 'Final balance',
                  })}
                </span>
              </div>
              <div className="font-semibold text-gray-900">
                {formatToMoney(tooltip.value, currency)}
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(tooltip.date + '-01', 'month-year', i18n.language)}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-center gap-6">
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                {index === 0 ? (
                  <div
                    className="h-1 w-5"
                    style={{ backgroundColor: item.color }}
                  />
                ) : (
                  <div
                    className="h-3 w-6 rounded-lg"
                    style={{ backgroundColor: item.color }}
                  />
                )}
                <span className="text-sm font-medium text-blue-gray-muted">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div
          className="mx-auto mt-8 px-8 text-center font-semibold text-primary-medium"
          data-testid="no-data-message"
        >
          <p>{t('common.no-data', { ns: 'common' })}</p>
        </div>
      )}
    </ChartContainer>
  );
};
